import { InjectProperty } from '@jack-henry/frontend-utils/di';
import {
    FdlFieldDefinitions,
    FdlRecordEvent,
    FieldType,
    listenForRecordEvent,
    Record,
} from '@treasury/FDL';
import { ConfigurationService } from '@treasury/core/config';
import '@treasury/omega/components/omega-field.js';
import { boolean, string } from '@treasury/policy/primitives';
import { TmBaseComponent } from '@treasury/presentation';
import { Nullable } from '@treasury/utils';
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

interface FormFields {
    companyId: string;
    loginId: string;
    institution: string;
    password?: string;
    useRefreshTokens: boolean;
    code: string;
}

export const tagName = 'user-verification';
@customElement(tagName)
export class UserVerification extends TmBaseComponent {
    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @state()
    private record = this.makeRecord();

    @state()
    private formActionsDisabled = false;

    @state()
    private loading = false;

    private fields: FdlFieldDefinitions<Nullable<FormFields>> = {
        companyId: string.with
            .label('Company ID')
            .and.placeholder('Enter Company ID')
            .thatHas.maxLength(25)
            .and.minLength(1)
            .thatIs.required() as FieldType<string | null>,
        loginId: string.with
            .label('Login ID')
            .and.placeholder('Enter Login ID')
            .thatHas.maxLength(25)
            .and.minLength(1)
            .thatIs.required() as FieldType<string | null>,
        institution: string as FieldType<string | null>,
        password: string as FieldType<string | null | undefined>,
        useRefreshTokens: boolean as FieldType<boolean | null>,
        code: string as FieldType<string | null>,
    };

    private get resetCode() {
        const codeParameter = new URLSearchParams(window.location.search).get('code');
        if (!codeParameter) {
            return '';
        }
        return decodeURIComponent(codeParameter);
    }

    public connectedCallback() {
        super.connectedCallback();
        this.formActionsDisabled = true;
        this.record = new Record(this.fields, {
            // eslint-disable-next-line no-restricted-syntax
            companyId: null,
            // eslint-disable-next-line no-restricted-syntax
            loginId: null,
            code: this.resetCode,
            useRefreshTokens: false,
            institution: this.config.institutionId,
        });
        const { unsubscribe } = listenForRecordEvent(this.record, FdlRecordEvent.Change, () => {
            this.isFormInvalid();
        });

        this.addCleanupTasks(unsubscribe);
    }

    protected render() {
        return html`
            <summary>
                To verify your identity and process with a password change, please enter your
                Company ID and Login ID and provide the answer(s) for your security question(s).
            </summary>
            <form>
                <omega-field field="companyId" .record=${this.record}></omega-field>
                <omega-field field="loginId" .record=${this.record}></omega-field>
            </form>
            <footer>
                <omega-button
                    id="log-in"
                    type="primary"
                    ?loading=${this.loading}
                    ?disabled=${this.formActionsDisabled}
                    @click=${this.submitUser}
                >
                    Submit
                </omega-button>
                <omega-button
                    id="reset"
                    type="link"
                    ?disabled=${this.formActionsDisabled}
                    @click=${this.reset}
                >
                    Reset
                </omega-button>
            </footer>
        `;
    }

    private makeRecord() {
        return new Record(this.fields, {
            // eslint-disable-next-line no-restricted-syntax
            companyId: null,
            // eslint-disable-next-line no-restricted-syntax
            loginId: null,
            code: this.resetCode,
            useRefreshTokens: false,
            institution: this.config.institutionId,
        });
    }

    private submitUser() {
        this.dispatchEvent(
            new CustomEvent('submit', {
                detail: { formValues: this.record.values, record: this.record },
            })
        );
    }

    private reset() {
        this.record.reset();
    }

    private isFormInvalid() {
        this.formActionsDisabled = !this.record.isValid() || !this.record.hasRequiredValues();
    }

    public static get styles() {
        return [
            css`
                footer {
                    padding: 4px 15px;
                    border-top: 1px solid #ccc;
                }
                summary,
                form {
                    padding: 15px;
                }
                omega-field {
                    --omega-field-label-width: 100px;
                    --omega-field-control-width: 250px;
                    max-width: 360px;
                    margin-bottom: 15px;
                }
                omega-field:last-of-type {
                    margin-bottom: 0;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: UserVerification;
    }
}
